import Modal from '@/components/base/Modal';
import StatusBadge from '@/components/base/StatusBadge';
import { formatDate, getWeekday } from '@/lib/date';
import { getStatusMeta, lessonStatusMap } from '@/lib/statusMap';
import type { StudentLesson, StudentGroup } from '@/hooks/useStudentData';

interface LessonDetailModalProps {
  open: boolean;
  onClose: () => void;
  lesson: StudentLesson | null;
  group: StudentGroup | null;
}

export default function StudentLessonDetailModal({
  open,
  onClose,
  lesson,
  group,
}: LessonDetailModalProps) {
  if (!lesson) return null;

  const status = getStatusMeta(lesson.status, lessonStatusMap);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={lesson.title}
      subtitle={group ? `${group.name}${lesson.level ? ` · ${lesson.level}` : ''}` : 'Ders Detayı'}
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge meta={status} />
          <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
            {lesson.type === 'birebir' ? 'Birebir Ders' : 'Grup Dersi'}
          </span>
          {lesson.live_link && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              <i className="ri-live-line" /> Canlı
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-white p-3.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
              <i className="ri-calendar-line" /> Tarih
            </p>
            <p className="mt-1 text-sm font-medium capitalize text-foreground-900">
              {lesson.date ? `${formatDate(lesson.date)} · ${getWeekday(lesson.date)}` : '—'}
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-white p-3.5">
            <p className="flex items-center gap-2 text-xs font-semibold text-foreground-500">
              <i className="ri-time-line" /> Saat
            </p>
            <p className="mt-1 text-sm font-medium text-foreground-900">
              {lesson.start_time ?? '--:--'} - {lesson.end_time ?? '--:--'}
            </p>
          </div>
        </div>

        {lesson.subject && (
          <div>
            <p className="text-xs font-semibold text-foreground-500">Konu</p>
            <p className="mt-1 text-sm text-foreground-800">{lesson.subject}</p>
          </div>
        )}

        {lesson.description && (
          <div>
            <p className="text-xs font-semibold text-foreground-500">Açıklama</p>
            <p className="mt-1 text-sm text-foreground-700">{lesson.description}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {lesson.has_record && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
              <i className="ri-vidicon-line" /> Kayıt Var
            </span>
          )}
          {lesson.has_document && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
              <i className="ri-file-pdf-2-line" /> Doküman Var
            </span>
          )}
          {lesson.has_assignment && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
              <i className="ri-task-line" /> Ödev Atandı
            </span>
          )}
        </div>

        {lesson.live_link && (
          <a
            href={lesson.live_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-600"
          >
            <i className="ri-vidicon-line text-base" />
            Canlı Derse Katıl
          </a>
        )}
      </div>
    </Modal>
  );
}